import { Subject } from 'rxjs';
import { Card } from './cat.model';
import {
  PaginationParams,
  DataElement,
  ReferencePhrase,
  AddInstr,
  Doc,
  PhraseWord,
  CatMatch,
  ProcessRunStatusEnum,
  CardEntryMatch,
  BoundingBoxMatch,
  AnnotationElement,
  GrammarSpellingIssue,
  ReferenceIssue,
  AssociatedTextPhrase,
  AssociatedTextFinding,
  AssociatedImage,
  AssociatedImageFinding,
  ConsistencyIssuesToUpdateDto,
  BoundingBoxAnchorResult,
  AnchorResult,
  CopiedAnchorResult,
} from './common';
import { EmbeddedObjectAnnotation } from './embedded-object-annotation';
import { ConsistencyIssue } from '@shared/components/issues/factory/model/consistency-issue.models';
import { ComplianceIssue } from '@shared/components/issues/factory/model/compliance-issue.models';
import { FairBalanceIssue } from '@shared/components/issues/factory/model/fair-balance-issue.models';
import { DocMetadataValues } from 'src/app/add-doc-metadata/model/doc-metadata';

export class RevDoc extends Doc {
  lastVersionId: number;
  createDate: Date;
  createDateFormatted: string;
  productId: any;
  productDescr: string;
  catId: number;
  catDescr: string;
  catVersionId: number;
  newCatVersionSw: boolean;
  runStatusCode: string;
  runStatusMessage: string;
  passedCnt: number;
  failedCnt: number;
  dismissedCnt: number;
  newClaimCnt: number;
  claimVariantCnt: number;
  markedCnt: number;
  stetCnt: number;
  notPassedVarCnt: number;
  notPassedSevCnt: number;
  newContentCnt: number;
  versionId: number;
  progressPct: number;
  totCnt: number;
  statusCode: string;
  statusDescr: string;
  statusDate: string;
  fileName: string;
  docType: string;
  parentDocId: number;
  actCatVersionId: number;
  uploadUserDescr: string;
  assetType: DataElement;
  origDocId: number;
  disableCanvasViewer: boolean;
  catSystemVersion?: number | null;
  docVersions: RevDoc[] = [];
  metadata?: {[key: string]: string[]};

  get isProcessRunning(): boolean {
    return (
      this.runStatusCode == ProcessRunStatusEnum.RUNNING ||
      this.runStatusCode == ProcessRunStatusEnum.PENDING ||
      this.runStatusCode == ProcessRunStatusEnum.PENDING_CHECKS
    );
  }

  constructor() {
    super();
    this.docObjectType = 'REV';
  }
}

export class RevDocsSearchParams extends PaginationParams {
  envId?: number;
  docId?: number;
  docName?: string;
  docIdName?: string;
  statusCode?: string;
  product?: DataElement;
  includeInactiveSw?: boolean;

  constructor() {
    super();
  }
}

export class RevRule extends AnnotationElement {
  revRuleId: number;
  phraseId: number;
  revRuleType: string;
  revRuleTypeDescr: string;
  ruleDescr: string;
  pageNum: number;
  revStatusCode: string;
  revStatusDescr: string;
  ruleUserStatus: DataElement;
  similarityCode: string;
  similarityDescr: string;
  similarityPct: number;
  phraseSimilarityPct: number;
  isRefSw: boolean;
  references: ReferencePhrase[];
  consistencyIssues: ConsistencyIssue[];
  consistencyIssuesDirty: boolean = false;
  fairBalanceIssues: FairBalanceIssue[];
  referencedBy: ReferencePhrase[];
  associatedText: AssociatedTextPhrase[];
  associatedBy: AssociatedTextPhrase[];
  associatedImages: AssociatedImage[];
  addInstr: AddInstr[];
  ruleDispWords: PhraseWord[] = [];
  grammarSpellingIssues: GrammarSpellingIssue[];
  referenceIssues: ReferenceIssue[];
  assocTextFindings: AssociatedTextFinding[];
  assocImageFindings: AssociatedImageFinding[];
  formatMatchEntry: PhraseWord[] = [];
  formatMatchPhrase: PhraseWord[] = [];
  checkedSw: boolean;
  viewSw: boolean;
  ruleDiffParts: PhraseWord[] = [];
  ruleCatMatches: CatMatch[] = [];
  annElPassed: string;
  annElFailed: string;
  annElDismissed: string;
  annElMarkedForChanges: string;
  annElStet: string;
  annElNotPassedVar: string;
  comment: string;
  cardEntryMatch: CardEntryMatch;
  boundingBoxMatch: BoundingBoxMatch;
  imageId: number;
  imgUrl: string;
  cardMatch: CardMatch;
  cardMatchId: number;
  figureType: string;
  figureTypeDescr: string;
  figureSubType?: DataElement;
  revRuleContentType: string;
  revRuleContentTypeDesc: string;
  revRuleSimilarityEvent: string;
  revRuleSimilarityEventDesc: string;
  revRuleFindingEvents: string[] = [];
  revRuleActions: string[] = [];
  revRuleFindingCategoryDescs: [string, string][] = [];
  contextCatPhraseId?: number;
    typeCode: string;
  typeDescr: string;
  subTypeCode: string;
  subTypeDescr: string;
  original: string;
  parentPhraseId: number | null;
  isParentActionSelected: boolean = false;
  numOfContextCatPhrases: number = 0;
  numberOfReferencedBy: number = 0;
  numberOfAssociatedBy: number = 0;
  numberOfFootnotes: number = 0;
  numberOfTables: number = 0;
  numberOfImages: number = 0;

  children: {[type: string]: RevRule[]} = {};
  contentChildren: RevRule[] = [];
  imageChildren: RevRule[] = [];
  //footnotesChildren: RevRule[] = [];
  referencesInFootnotesSection: ReferencePhrase[] = [];
  consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto;
  showAssociatedTextSection: boolean = true;
  showReferencesSection: boolean = true;
  showFootnotesSection: boolean = true;
  showAssociatedImagesSection: boolean = true;
  showAssociatedBySection: boolean = true;
  showReferencedBySection: boolean = true;
  showTablesSection: boolean = true;
  showImages: boolean = true;
  showAssociatedTablesSection: boolean = true;
  figureCategory: string;
  complianceIssues: ComplianceIssue[];
  fullyFetched: boolean = false;
  boundingBoxAnchorsToAdd: BoundingBoxAnchorResult[] = [];
  //copiedAnchors: CopiedAnchorResult[] = [];
  propagateActionFromParent = new Subject();  
  anchorsPathsToUpdate: {
    anchors: {
      ref_doc_id: number;
      ref_doc_anchor_id: number;
      data: {
        path: string
      }
    }[]
  } | null = null;
  disableConsistencyCheckResultsActions: boolean = false;

  get annIdPrefix() {
    return this.imageId !== null ? "img" : null;
  }

  saveOriginal() {

    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original" || key === 'propagateActionFromParent') {
         return undefined; // Skip this property
      }
      return value; 
    })
    this.ruleCatMatches?.forEach((catMatch) => catMatch.saveOriginal());     
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as RevRule;
    this.comment = original.comment;
    this.consistencyIssues = original.consistencyIssues;
    this.fairBalanceIssues = original.fairBalanceIssues;
    this.complianceIssues = original.complianceIssues;
    this.referenceIssues = original.referenceIssues;
    if (original.references != null && original.references.length > 0) {
      this.references = original.references.map((r) => {
        let newRef = new ReferencePhrase();
        Object.assign(newRef, r);
        if (r.anchors && r.anchors.length > 0) {

          newRef.anchors = r.anchors.map((a) => {

            let restoreAnchorResult = new AnchorResult(a.issueId, a.refDocAnchorId, a.refDocId, a.embeddedObjectAnnotation);
            return restoreAnchorResult;
            
          });
        }
        newRef.saveOriginal();
        return newRef;
      });
    } else {
      this.references = [];
    }
    this.disableConsistencyCheckResultsActions = original.disableConsistencyCheckResultsActions;
    this.associatedImages = original.associatedImages;
    this.boundingBoxAnchorsToAdd = [];
    //this.copiedAnchors = [];
    this.consistencyIssuesToUpdate = original.consistencyIssuesToUpdate;
    this.anchorsPathsToUpdate = original.anchorsPathsToUpdate;
    this.ruleCatMatches?.forEach((catMatch) => catMatch.restore());
  }

  get isImage(): boolean {
    return this.imageId !== null;
  }
}

export class CardMatch {
  cardMatchId: number;
  mCardId: number;
  mDocId: number;
  mBoxId: number;
  mPhraseId: number;
  mCard: Card;
  matchStatusCode: string;
  matchStatusDescr: string;
  cardId: number;
  boxId: number;
  card: Card;
  phraseId: number;
  catPhraseId: number;
}

export class ImageMatch {
  cardImageMatchId: number;
  imageId: number;
  docId: number;
  boxId: number | null;
  catBoxId: number;
  catImageId: number;
  catCardId: number;
  s3Key: string;
  catS3Key: string;
  similarity: number;
  matchStatusCode: string;
  matchStatusDescr: string;
}

export class RevRulesSearchParams {
  phrase: string;
  ruleType: DataElement;
  ruleStatus: DataElement;
  contentType: DataElement;
  matchType: DataElement;
  subTypeList: string[] = [];
  currLoadedRuleNum?: number;
}

export class RevCatalog {
  catId: number;
  catDescr: string;
  lastVersionId: number;
  catVersionId: number;
  catAssetTypeList: DataElement[] = [];
  catType: string;
  catTypeDescr: string;
  systemVersion?: number | null;
}

export class DirectLinkDoc {
  docId: number;
  page: number;
  ruleId: number;
  cardId: number;
  dlDocHandled: boolean;
  dlPageHandled: boolean;
  dlRuleHandled: boolean;
  dlCardHandled: boolean;
}

/** ENUMS **/

export class RevDocStatusEnum {
  public static UNDERWAY: string = 'UNDERWAY';
  public static FINAL: string = 'FINAL';
  public static COMPLETED: string = 'COMPLETED';
  public static INACTIVE: string = 'INACTIVE';
}

export class RevStatus {
  public static MARKED_FOR_CHANGES: string = 'MARKED_FOR_CHANGES';
  public static DISMISSED: string = 'DISMISSED';
  public static NEW_CLAIM: string = 'NEW_CLAIM';
  public static CLAIM_VARIANT: string = 'CLAIM_VARIANT';
  public static PASSED: string = 'PASSED';
  public static NOT_PASSED: string = 'NOT_PASSED';
  public static STET: string = 'STET';
  public static NOT_PASSED_VAR: string = 'NOT_PASSED_VAR';
  public static NOT_PASSED_SEV: string = 'NOT_PASSED_SEV';
  public static NEW_CONTENT: string = 'NOT_PASSED_NEW_CONTENT';
}

export type PageToolRevPhrase = {
  phrase_id: number;
  phrase_text: string;
  page_order_id: number;
  phrase_content_type: string;
  has_rev_rule: boolean
}

export type PageToolRevImage = {
  image_id: number;
  s3_key: string;
  page_order_id: number;
  figure_type: string;
  has_rev_rule: boolean
  imgUrl: string;
}
